/**
 * Multi-Institution Configuration
 * Central source of truth for all supported institutions and their document types
 */

export interface DocumentType {
  value: string;
  label: string;
  hasSubtypes?: boolean;
  subtypes?: DocumentSubtype[];
}

export interface DocumentSubtype {
  value: string;
  label: string;
}

export interface Institution {
  code: string;
  name: string;
  shortName: string;
  description: string;
  types: DocumentType[];
  color: string; // Tailwind color for UI
  icon?: string; // Optional icon identifier
}

export const INSTITUTIONS: Record<string, Institution> = {
  SII: {
    code: 'SII',
    name: 'Servicio de Impuestos Internos',
    shortName: 'SII',
    description: 'Jurisprudencia administrativa tributaria',
    color: 'blue', // Azul - SII
    types: [
      {
        value: 'oficio',
        label: 'Oficio',
        hasSubtypes: true,
        subtypes: [
          { value: 'renta', label: 'Renta' },
          { value: 'iva', label: 'IVA' },
          { value: 'otras', label: 'Otras' },
        ],
      },
      {
        value: 'circular',
        label: 'Circular',
      },
      {
        value: 'resolucion',
        label: 'Resolución',
      },
    ],
  },
  TTA: {
    code: 'TTA',
    name: 'Tribunales Tributarios y Aduaneros',
    shortName: 'TTA',
    description: 'Sentencias de los Tribunales Tributarios y Aduaneros',
    color: 'blue',
    types: [{ value: 'jurisprudencia', label: 'Sentencia' }],
  },
  BCN: {
    code: 'BCN',
    name: 'Biblioteca del Congreso Nacional',
    shortName: 'BCN',
    description: 'Leyes, decretos leyes, DFL y decretos tributarios',
    color: 'blue',
    types: [
      { value: 'ley', label: 'Ley' },
      { value: 'dl', label: 'Decreto ley' },
      { value: 'dfl', label: 'DFL' },
      { value: 'decreto', label: 'Decreto' },
    ],
  },
};

/**
 * Colecciones (bibliotecas por tema, D-UI-3): cada una agrupa tipos de una institución.
 * El orden de las claves es el orden en que se muestran.
 */
export type CollectionKey = 'adm' | 'jud' | 'ley' | 'conv';

export interface Collection {
  key: CollectionKey;
  label: string;
  /** Código de la institución (mayúsculas) dueña de los documentos. */
  institution: string;
  /** Valores de `tipo` en minúscula, como los usa la API. */
  tipos: string[];
  description: string;
}

export const COLLECTIONS: Record<CollectionKey, Collection> = {
  adm: {
    key: 'adm',
    label: 'Jurisprudencia administrativa',
    institution: 'SII',
    tipos: ['oficio', 'circular', 'resolucion'],
    description: 'Oficios, circulares y resoluciones del SII',
  },
  jud: {
    key: 'jud',
    label: 'Sentencias del TTA',
    institution: 'TTA',
    tipos: ['jurisprudencia'],
    description: 'Sentencias de los Tribunales Tributarios y Aduaneros',
  },
  ley: {
    key: 'ley',
    label: 'Leyes y normas',
    institution: 'BCN',
    tipos: ['ley', 'dl', 'dfl', 'decreto'],
    description: 'Leyes, decretos leyes, DFL y decretos tributarios',
  },
  conv: {
    key: 'conv',
    label: 'Convenios',
    institution: 'SII',
    tipos: ['convenio'],
    description: 'Convenios para evitar la doble tributación',
  },
};

/** Direcciones antiguas `/biblioteca/<institución>` y la colección a la que ahora corresponden. */
export const LEGACY_LIBRARY_REDIRECTS: Record<string, CollectionKey> = {
  sii: 'adm',
  tta: 'jud',
  bcn: 'ley',
};

/** Devuelve la colección si `key` es una clave válida (insensible a mayúsculas: `/biblioteca/ADM` = `adm`). */
export const getCollection = (key: string | undefined | null): Collection | undefined => {
  const k = (key || '').toLowerCase();
  if (!k || !Object.prototype.hasOwnProperty.call(COLLECTIONS, k)) return undefined;
  return COLLECTIONS[k as CollectionKey];
};

/** Las colecciones en su orden fijo. */
export const getAllCollections = (): Collection[] => Object.values(COLLECTIONS);

/** Colección a la que pertenece un documento (insensible a mayúsculas); undefined si no se conoce. */
export const collectionForDocument = (
  institutionCode: string | undefined | null,
  tipo?: string | null
): Collection | undefined => {
  const inst = (institutionCode || '').toUpperCase();
  if (inst === 'SII') {
    return (tipo || '').toLowerCase() === 'convenio' ? COLLECTIONS.conv : COLLECTIONS.adm;
  }
  if (inst === 'TTA') return COLLECTIONS.jud;
  if (inst === 'BCN') return COLLECTIONS.ley;
  return undefined;
};

/** Colección a la que redirigir una dirección antigua (`sii`, `TTA`...); undefined si no es un código heredado. */
export const legacyRedirectFor = (code: string | undefined | null): CollectionKey | undefined => {
  const key = (code || '').toLowerCase();
  return Object.prototype.hasOwnProperty.call(LEGACY_LIBRARY_REDIRECTS, key) ? LEGACY_LIBRARY_REDIRECTS[key] : undefined;
};

// Helper functions
export const getInstitution = (code: string): Institution | undefined => {
  return INSTITUTIONS[code.toUpperCase()];
};

export const getAllInstitutions = (): Institution[] => {
  return Object.values(INSTITUTIONS);
};

export const getInstitutionTypes = (institutionCode: string): DocumentType[] => {
  const institution = getInstitution(institutionCode);
  return institution?.types || [];
};

export const getDocumentTypeLabel = (institutionCode: string, typeValue: string): string => {
  const types = getInstitutionTypes(institutionCode);
  const type = types.find((t) => t.value === typeValue);
  return type?.label || typeValue;
};

export const getDocumentSubtypeLabel = (
  institutionCode: string,
  typeValue: string,
  subtypeValue: string
): string => {
  const types = getInstitutionTypes(institutionCode);
  const type = types.find((t) => t.value === typeValue);
  const subtype = type?.subtypes?.find((s) => s.value === subtypeValue);
  return subtype?.label || subtypeValue;
};

export const hasSubtypes = (institutionCode: string, typeValue: string): boolean => {
  const types = getInstitutionTypes(institutionCode);
  const type = types.find((t) => t.value === typeValue);
  return type?.hasSubtypes || false;
};

/**
 * Validates if a document type is valid for an institution
 */
export const isValidDocumentType = (institutionCode: string, typeValue: string): boolean => {
  const types = getInstitutionTypes(institutionCode);
  return types.some((t) => t.value === typeValue);
};

/**
 * Validates if a subtype is valid for a document type
 */
export const isValidSubtype = (
  institutionCode: string,
  typeValue: string,
  subtypeValue: string
): boolean => {
  const types = getInstitutionTypes(institutionCode);
  const type = types.find((t) => t.value === typeValue);
  return type?.subtypes?.some((s) => s.value === subtypeValue) || false;
};

/**
 * Generate storage path for R2
 * Format: {institution}/{year}/{tipo}/{subtipo?}/filename.pdf
 */
export const generateStoragePath = (
  institutionCode: string,
  year: number,
  tipo: string,
  filename: string,
  subtipo?: string
): string => {
  const basePath = `${institutionCode.toLowerCase()}/${year}/${tipo}`;

  if (subtipo) {
    return `${basePath}/${subtipo}/${filename}`;
  }

  return `${basePath}/${filename}`;
};

/**
 * Institution Color System
 * Pre-computed Tailwind classes for JIT compatibility
 * Use getInstitutionColors() instead of dynamic class generation
 */
export interface InstitutionColors {
  // Gradients
  gradient: string;
  gradientHover: string;
  // Backgrounds
  bg: string;
  bgLight: string;
  bgHover: string;
  // Icons
  iconBg: string;
  iconColor: string;
  // Text
  text: string;
  textLight: string;
  textDark: string;
  // Badges
  badgeBg: string;
  badgeText: string;
  // Borders
  border: string;
  borderLight: string;
  // Buttons
  buttonBg: string;
  buttonHover: string;
  // Shadows
  shadow: string;
}

export const INSTITUTION_COLORS: Record<string, InstitutionColors> = {
  SII: {
    gradient: 'from-blue-600 to-blue-700',
    gradientHover: 'from-blue-700 to-blue-800',
    bg: 'bg-blue-600',
    bgLight: 'bg-blue-50',
    bgHover: 'hover:bg-blue-700',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    text: 'text-blue-700',
    textLight: 'text-blue-500',
    textDark: 'text-blue-800',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-700',
    border: 'border-blue-200',
    borderLight: 'border-blue-100',
    buttonBg: 'bg-blue-600',
    buttonHover: 'hover:bg-blue-700',
    shadow: 'shadow-blue-500/30',
  },
};

// TTA y BCN comparten el azul de la marca, como el resto de las instituciones (sin cambio visual).
INSTITUTION_COLORS.TTA = INSTITUTION_COLORS.SII;
INSTITUTION_COLORS.BCN = INSTITUTION_COLORS.SII;

// Default colors for unknown institutions
const DEFAULT_COLORS: InstitutionColors = {
  gradient: 'from-slate-500 to-slate-600',
  gradientHover: 'from-slate-600 to-slate-700',
  bg: 'bg-slate-600',
  bgLight: 'bg-slate-50',
  bgHover: 'hover:bg-slate-700',
  iconBg: 'bg-slate-100',
  iconColor: 'text-slate-600',
  text: 'text-slate-700',
  textLight: 'text-slate-500',
  textDark: 'text-slate-800',
  badgeBg: 'bg-slate-100',
  badgeText: 'text-slate-700',
  border: 'border-slate-200',
  borderLight: 'border-slate-100',
  buttonBg: 'bg-slate-600',
  buttonHover: 'hover:bg-slate-700',
  shadow: 'shadow-slate-500/30',
};

/**
 * Get pre-computed Tailwind color classes for an institution
 * @param institutionCode - The institution code (e.g., 'SII', 'TTA', 'BCN')
 * @returns InstitutionColors object with all color classes
 */
export const getInstitutionColors = (institutionCode: string): InstitutionColors => {
  const normalizedCode = institutionCode.toUpperCase();
  return INSTITUTION_COLORS[normalizedCode] || DEFAULT_COLORS;
};

/**
 * @deprecated Use getInstitutionColors() instead for JIT compatibility
 * Get Tailwind color classes for institution (legacy)
 */
export const getInstitutionColorClasses = (institutionCode: string) => {
  const institution = getInstitution(institutionCode);
  const color = institution?.color || 'gray';

  return {
    bg: `bg-${color}-50`,
    border: `border-${color}-200`,
    text: `text-${color}-700`,
    badgeBg: `bg-${color}-100`,
    badgeText: `text-${color}-800`,
    buttonBg: `bg-${color}-600`,
    buttonHover: `hover:bg-${color}-700`,
  };
};
